/**
 * Communication Log — what was sent, to whom, by whom.
 *
 * The record half of the module. Two views of the same rows: the sends, each one press of
 * Send with its tally, and the letters, one row per message.
 *
 * A failure is kept rather than deleted, and that is the point of reading this page: "we
 * wrote and it bounced" and "we never wrote" look identical on the Email Partner Units
 * list — both show no contact — and only here do they come apart. The binding's own error
 * is repeated verbatim because E_RECIPIENT_SUPPRESSED and E_RATE_LIMIT_EXCEEDED ask for
 * entirely different things: one address will never work again, the other will tomorrow.
 */
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi, qs, fetchAllRows } from '../lib/api'
import {
  Card, Badge, Notice, Empty, QueryState, SearchInput, Tabs, useSort, useToast,
  fmtInt, fmtDateTime, downloadCsv,
} from '../components/ui'
import { PagedTable } from '../components/DataTable'
import { useRelabel, useCode } from '../lib/terms'

const STATUS = {
  sent: { tone: 'ok', label: 'sent' },
  failed: { tone: 'danger', label: 'failed' },
  pending: { tone: 'warn', label: 'pending' },
  sending: { tone: 'warn', label: 'sending' },
}

export default function CommunicationLog({ user }) {
  const t = useRelabel()
  const code = useCode()
  const [params, setParams] = useSearchParams()
  const toast = useToast()
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const { sort, dir, onSort: sortBy } = useSort('when', 'desc', ['when'])
  const onSort = (key) => {
    sortBy(key)
    setPage(1)
  }
  const status = params.get('status') ?? ''

  const allowed = user.caps.manageSettings
  const filters = { q: q || null, status: status || null, sort, dir }
  const query = useApi(
    ['comm-log', q, status, page, limit, sort, dir],
    `communications${qs({ ...filters, page, limit })}`,
    { enabled: allowed, keepPreviousData: true }
  )

  // Every letter these filters match, in this order — not only the page on screen.
  const exportAll = async () => {
    try {
      const all = await fetchAllRows('communications', filters)
      downloadCsv('communication-log.csv', all.rows, [
        { key: 'sent_at', label: 'Sent' },
        { key: 's4ca_code', label: 'S4CA Code' },
        { key: 'display_name', label: 'Partner Unit' },
        { key: 'to_email', label: 'To' },
        { key: 'subject', label: 'Subject' },
        { key: 'status', label: 'Status' },
        { key: 'error', label: 'Error' },
        { key: 'sent_by', label: 'Sent by' },
        { key: 'template_name', label: 'Letter' },
      ])
      if (all.total > all.rows.length) toast.info(`Exported the first ${fmtInt(all.rows.length)} of ${fmtInt(all.total)} rows`)
    } catch (err) {
      toast.error(err)
    }
  }

  if (!allowed) {
    return (
      <PageHeader title={t('Communication Log')}>
        <Notice tone="warn" icon="lock" title="Not available to your role">
          The record of what the archive has sent, and to which addresses, is kept to owners
          and administrators. You are signed in as <strong>{user.roleLabel}</strong>.
        </Notice>
      </PageHeader>
    )
  }

  const setStatus = (v) => {
    const next = new URLSearchParams(params)
    if (v) next.set('status', v)
    else next.delete('status')
    setParams(next, { replace: true })
    setPage(1)
  }

  const search = (v) => {
    setQ(v)
    setPage(1)
  }

  const columns = [
    { key: 'when', label: 'When', sortKey: 'when', cellClass: 'small subtle', render: (r) => fmtDateTime(r.sent_at ?? r.created_at) },
    {
      key: 's4ca_code',
      label: 'Partner Unit',
      sortKey: 's4ca_code',
      cellClass: 'small',
      // The unit may since have left the registry, and the log still names it: the code
      // is kept beside the link, not instead of it.
      render: (r) => (
        <>
          {r.partner_id ? (
            <Link to={`/partners/${r.partner_id}`} className="mono">
              {code(r.s4ca_code)}
            </Link>
          ) : (
            <span className="mono subtle" title="No longer in the registry">
              {code(r.s4ca_code)}
            </span>
          )}{' '}
          {code(r.display_name ?? '')}
        </>
      ),
    },
    { key: 'to_email', label: 'To', sortKey: 'to_email', cellClass: 'small subtle' },
    {
      key: 'subject',
      label: 'Subject',
      sortKey: 'subject',
      cellClass: 'small',
      render: (r) => (
        <>
          {r.subject}
          {r.error && (
            <div className="small" style={{ color: 'var(--danger)' }}>
              {r.error}
            </div>
          )}
        </>
      ),
    },
    { key: 'sent_by', label: 'Sent by', sortKey: 'sent_by', cellClass: 'small subtle' },
    {
      key: 'status',
      label: '',
      render: (r) => <Badge tone={STATUS[r.status]?.tone}>{STATUS[r.status]?.label ?? r.status}</Badge>,
    },
  ]

  return (
    <PageHeader
      title={t('Communication Log')}
      sub="Every letter the archive has sent, and who sent it"
      actions={
        <button className="btn btn-sm" onClick={exportAll} disabled={!query.data?.total}>
          ↓ CSV
        </button>
      }
    >
      <QueryState query={query}>
        {(d) => (
          <div className="col">
            {!!d.batches.length && (
              <Card title="Sends" sub="One row per press of Send, newest first">
                <div className="table-wrap">
                  <table className="data compact">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Letter</th>
                        <th>Sent by</th>
                        <th className="right">To</th>
                        <th className="right">Sent</th>
                        <th className="right">Failed</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {d.batches.map((b) => (
                        <tr key={b.id}>
                          <td className="small subtle">{fmtDateTime(b.created_at)}</td>
                          <td className="small">
                            {b.template_name ?? <span className="subtle">a one-off note</span>}
                            <div className="small subtle">{b.subject}</div>
                          </td>
                          <td className="small subtle">{b.sent_by}</td>
                          <td className="num">{fmtInt(b.total)}</td>
                          <td className="num">{fmtInt(b.sent)}</td>
                          <td className="num">{b.failed ? fmtInt(b.failed) : '—'}</td>
                          <td>
                            {b.pending ? (
                              /* A send that stopped part way. The rows are still queued, so
                                 it is finishable rather than lost — which is the reason the
                                 queue is written down before anything leaves. */
                              <Badge tone="warn">{fmtInt(b.pending)} unsent</Badge>
                            ) : (
                              <Badge tone="muted">{b.kind}</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* The letters' own controls, the same as every list's: the search box on its
                own line, then the status tabs. */}
            <SearchInput
              value={q}
              onChange={search}
              placeholder="A code, an address, a subject, a sender…"
              aria-label="Search the communication log"
            />
            <Tabs
              tabs={[
                { key: '', label: 'All' },
                { key: 'sent', label: 'Sent' },
                { key: 'failed', label: 'Failed' },
                { key: 'pending', label: 'Unsent' },
              ]}
              active={status}
              onChange={setStatus}
            />

            {!d.rows.length ? (
              <Empty title="Nothing here yet">
                {q || status
                  ? 'No letter matches that.'
                  : `Nothing has been sent. ${t('Email Partner Units')} is where a letter goes out.`}
              </Empty>
            ) : (
              <Card
                title={`${fmtInt(d.total)} Letter${d.total === 1 ? '' : 's'}`}
              >
                <PagedTable
                  compact
                  data={d}
                  columns={columns}
                  sort={sort}
                  dir={dir}
                  onSort={onSort}
                  onPage={setPage}
                  onLimit={(n) => {
                    setLimit(n)
                    setPage(1)
                  }}
                />
              </Card>
            )}
          </div>
        )}
      </QueryState>
    </PageHeader>
  )
}
